import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CheckCircle2,
  ImageOff,
  Package,
  Plus,
  RefreshCw,
  Search,
  Store,
  Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  integrationsList,
  productsImport,
  productsList,
  productsManualCreate,
  productsRemoteList,
} from "../../api/auth";
import {
  EmptyState,
  FeedbackMessage,
  getApiErrorMessage,
  LoadingState,
} from "../ui/Feedback";

const EMPTY_MANUAL_FORM = {
  title: "",
  sku: "",
  price: "",
  image_url: "",
  source_connection_id: "",
};

function ProductImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-slate-800">
        <ImageOff size={20} aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 object-cover dark:border-slate-700"
    />
  );
}

export default function ProductsCatalog() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("import");
  const [connections, setConnections] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [remoteProducts, setRemoteProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [customTitles, setCustomTitles] = useState({});
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);

  const loadCatalog = async () => {
    const response = await productsList();
    setCatalogProducts(Array.isArray(response.data) ? response.data : []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [connectionsResponse] = await Promise.all([
          integrationsList(),
          loadCatalog(),
        ]);
        const supported = (connectionsResponse.data || []).filter(
          (connection) =>
            connection.is_active && ["shopify", "youcan"].includes(String(connection.platform).toLowerCase())
        );
        setConnections(supported);
        if (supported.length) {
          setSourceId(String(supported[0].id));
          setManualForm((current) => ({ ...current, source_connection_id: String(supported[0].id) }));
        }
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Impossible de charger le catalogue."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadRemoteProducts = async () => {
    if (!sourceId) return;
    setRemoteLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await productsRemoteList(sourceId);
      const products = response.data?.products || [];
      setRemoteProducts(products);
      setSelectedIds(new Set());
      setCustomTitles(
        Object.fromEntries(products.map((product) => [String(product.external_product_id), product.title]))
      );
    } catch (requestError) {
      setRemoteProducts([]);
      setError(getApiErrorMessage(requestError, "Impossible de récupérer les produits de cette boutique."));
    } finally {
      setRemoteLoading(false);
    }
  };

  useEffect(() => {
    loadRemoteProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const visibleRemoteProducts = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return remoteProducts;
    return remoteProducts.filter((product) =>
      [product.title, product.handle, product.external_product_id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized)
    );
  }, [query, remoteProducts]);

  const availableVisibleIds = visibleRemoteProducts
    .filter((product) => !product.is_imported)
    .map((product) => String(product.external_product_id));

  const toggleProduct = (product) => {
    if (product.is_imported) return;
    const id = String(product.external_product_id);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const allSelected = availableVisibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((current) => {
      const next = new Set(current);
      availableVisibleIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleImport = async () => {
    if (!selectedIds.size) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const products = Array.from(selectedIds).map((externalProductId) => ({
        external_product_id: externalProductId,
        title: (customTitles[externalProductId] || "").trim(),
      }));
      const response = await productsImport(sourceId, products);
      await Promise.all([loadCatalog(), loadRemoteProducts()]);
      setSuccess(`${response.data?.imported_count || 0} produit(s) ajouté(s) au catalogue.`);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "L’import des produits a échoué."));
    } finally {
      setSaving(false);
    }
  };

  const handleManualCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await productsManualCreate({
        ...manualForm,
        source_connection_id: manualForm.source_connection_id || null,
      });
      setSuccess("Le produit manuel a été ajouté au catalogue.");
      setManualForm((current) => ({ ...EMPTY_MANUAL_FORM, source_connection_id: current.source_connection_id }));
      await loadCatalog();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible d’ajouter ce produit."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Chargement du catalogue..." />;

  return (
    <div className="page-shell px-1 py-2">
      <header className="page-header-card flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow">Catalogue</p>
          <h1 className="mt-3 text-2xl font-semibold text-slate-950 dark:text-white">Produits</h1>
          <p className="mt-2 text-sm tone-muted">{catalogProducts.length} produit(s) dans Teqa</p>
        </div>
        <div className="inline-flex w-full rounded-lg border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-800 lg:w-auto">
          <button
            type="button"
            onClick={() => setMode("import")}
            className={`flex min-h-10 flex-1 items-center justify-center gap-2 rounded-md px-4 text-sm font-medium lg:flex-none ${mode === "import" ? "bg-white text-slate-950 shadow-sm dark:bg-slate-700 dark:text-white" : "text-slate-500"}`}
          >
            <Upload size={16} /> Importer
          </button>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={`flex min-h-10 flex-1 items-center justify-center gap-2 rounded-md px-4 text-sm font-medium lg:flex-none ${mode === "manual" ? "bg-white text-slate-950 shadow-sm dark:bg-slate-700 dark:text-white" : "text-slate-500"}`}
          >
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </header>

      {error ? <FeedbackMessage type="error" title="Action impossible" message={error} onClose={() => setError("")} /> : null}
      {success ? <FeedbackMessage type="success" title="Catalogue mis à jour" message={success} onClose={() => setSuccess("")} /> : null}

      {mode === "import" ? (
        <section className="space-y-4" aria-label="Importer des produits">
          {!connections.length ? (
            <EmptyState
              title="Aucune boutique compatible connectée"
              message="Connectez et activez une boutique Shopify ou YouCan pour afficher ses produits."
              action={<button type="button" className="btn-primary" onClick={() => navigate("/integrations/add")}><Store size={16} /> Connecter une boutique</button>}
            />
          ) : (
            <>
              <div className="toolbar-card grid gap-3 lg:grid-cols-[minmax(220px,320px)_1fr_auto]">
                <select
                  className="field-select"
                  value={sourceId}
                  onChange={(event) => setSourceId(event.target.value)}
                  aria-label="Boutique source"
                >
                  {connections.map((connection) => (
                    <option key={connection.id} value={connection.id}>
                      {connection.store_name} · {connection.platform}
                    </option>
                  ))}
                </select>
                <label className="relative">
                  <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input className="field-input pl-9" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un produit" />
                </label>
                <button type="button" className="btn-secondary" onClick={loadRemoteProducts} disabled={remoteLoading} title="Actualiser les produits">
                  <RefreshCw size={16} className={remoteLoading ? "animate-spin" : ""} />
                  <span>Actualiser</span>
                </button>
              </div>

              {remoteLoading ? (
                <LoadingState label="Synchronisation avec la boutique..." />
              ) : !remoteProducts.length ? (
                <EmptyState title="Aucun produit trouvé" message="La boutique sélectionnée ne renvoie encore aucun produit accessible." />
              ) : (
                <div className="surface-card p-0">
                  <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                    <button type="button" className="btn-ghost px-2" onClick={toggleAllVisible} disabled={!availableVisibleIds.length}>
                      <Check size={16} /> Tout sélectionner ({availableVisibleIds.length})
                    </button>
                    <button type="button" className="btn-primary" onClick={handleImport} disabled={!selectedIds.size || saving}>
                      {saving ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                      Importer {selectedIds.size ? `(${selectedIds.size})` : ""}
                    </button>
                  </div>

                  <div className="grid gap-px bg-slate-200 dark:bg-slate-700 md:grid-cols-2">
                    {visibleRemoteProducts.map((product) => {
                      const id = String(product.external_product_id);
                      const selected = selectedIds.has(id);
                      return (
                        <div
                          key={id}
                          className={`min-w-0 bg-white p-4 dark:bg-slate-900 ${product.is_imported ? "opacity-60" : ""}`}
                        >
                          <div className="flex min-w-0 gap-3">
                            <button
                              type="button"
                              onClick={() => toggleProduct(product)}
                              disabled={product.is_imported}
                              className={`mt-5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"} disabled:cursor-not-allowed`}
                              aria-label={selected ? "Désélectionner le produit" : "Sélectionner le produit"}
                            >
                              {selected || product.is_imported ? <Check size={13} /> : null}
                            </button>
                            <ProductImage src={product.image_url} alt={product.title} />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <p className="break-words text-sm font-semibold text-slate-900 dark:text-white">{product.title}</p>
                                {product.is_imported ? (
                                  <span className="badge badge--confirmed"><CheckCircle2 size={13} /> Déjà importé</span>
                                ) : null}
                              </div>
                              <p className="mt-1 break-all text-xs text-slate-500">ID {id}</p>
                              {selected ? (
                                <input
                                  className="field-input mt-3"
                                  value={customTitles[id] || ""}
                                  maxLength={255}
                                  onChange={(event) => setCustomTitles((current) => ({ ...current, [id]: event.target.value }))}
                                  aria-label={`Nom Teqa pour ${product.title}`}
                                />
                              ) : product.is_imported && product.imported_title !== product.title ? (
                                <p className="mt-2 text-xs text-slate-500">Nom Teqa : {product.imported_title}</p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      ) : (
        <form onSubmit={handleManualCreate} className="surface-card grid gap-5 lg:grid-cols-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Nom du produit
            <input required maxLength={255} className="field-input mt-2" value={manualForm.title} onChange={(event) => setManualForm({ ...manualForm, title: event.target.value })} />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Boutique liée
            <select className="field-select mt-2" value={manualForm.source_connection_id} onChange={(event) => setManualForm({ ...manualForm, source_connection_id: event.target.value })}>
              <option value="">Aucune boutique</option>
              {connections.map((connection) => <option key={connection.id} value={connection.id}>{connection.store_name} · {connection.platform}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            SKU
            <input maxLength={255} className="field-input mt-2" value={manualForm.sku} onChange={(event) => setManualForm({ ...manualForm, sku: event.target.value })} />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Prix
            <input type="number" min="0" step="0.01" className="field-input mt-2" value={manualForm.price} onChange={(event) => setManualForm({ ...manualForm, price: event.target.value })} />
          </label>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200 lg:col-span-2">
            URL de l’image
            <input type="url" maxLength={1000} className="field-input mt-2" value={manualForm.image_url} onChange={(event) => setManualForm({ ...manualForm, image_url: event.target.value })} />
          </label>
          <div className="flex justify-end lg:col-span-2">
            <button type="submit" className="btn-primary" disabled={saving || !manualForm.title.trim()}>
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Package size={16} />}
              Ajouter au catalogue
            </button>
          </div>
        </form>
      )}

      <section className="surface-card p-0" aria-label="Catalogue Teqa">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Catalogue Teqa</h2>
        </div>
        {!catalogProducts.length ? (
          <div className="p-5"><EmptyState title="Votre catalogue est vide" message="Les produits importés ou ajoutés manuellement apparaîtront ici." /></div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {catalogProducts.map((product) => (
              <div key={product.id} className="flex min-w-0 items-center gap-4 px-5 py-4">
                <ProductImage src={product.image_url} alt={product.title} />
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-semibold text-slate-900 dark:text-white">{product.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{product.platform === "manual" ? "Ajout manuel" : product.platform} · ID {product.external_product_id}</p>
                </div>
                <span className={`badge ${product.status === "active" ? "badge--active" : "badge--neutral"}`}>{product.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
